<template>
  <div id="nav">
    <el-form v-if="!loginState" :model="loginForm" :inline="true">
      <el-form-item label="Username" size="medium">
        <el-input v-model="loginForm.username"></el-input>
      </el-form-item>
      <el-form-item label="Password">
        <el-input type="password" v-model="loginForm.password"></el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="login()">Login</el-button>
      </el-form-item>
    </el-form>
    <template v-else>
      <el-button type="default" @click="refreshDevices()">Refresh</el-button>
      <el-button type="default" @click="logout()">Logout</el-button>
    </template>
  </div>
  <div id="devices">
    <div v-for="device in devicesSorted" :key="device.id">
      <el-card class="device" :style="device.data.online === false ? 'filter: opacity(0.65) grayscale(1);' : ''">
        <el-tooltip effect="light" :content="device.type" :offset="-20"
          :visible-arrow="false">
          <el-avatar :src="`device_icons/${device.type}.png`" shape="square">
            <img src="device_icons/default.png"/>
          </el-avatar>
        </el-tooltip>
        <span class="device-name">{{ device.name }}</span>
        <template v-if="device.type === 'scene'">
          <el-button type="default" circle size="large"
            class="trigger"
            @click="triggerScene(device);"
          ><i class="material-icons-round">play_arrow</i></el-button>
        </template>
        <template v-else>
          <el-button type="default" circle size="large"
            :class="device.data.state ? 'state-on' : 'state-off'"
            :disabled="!device.data.online"
            @click="toggleDevice(device);"
          ><i class="material-icons-round">{{ device.data.online ? 'power_settings_new' : 'cloud_off' }}</i></el-button>
        </template>
      </el-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Home'
}
</script>

<script setup="" >
/* eslint-disable no-unused-vars */
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from "element-plus"

import authService from '@/services/auth'
import tuyaService from '@/services/tuya'

const loginState = ref(false)
const devices = ref([])
const isRefreshing = ref(false)

const devicesSorted = computed(() => {
  const order = { true: 0, undefined: 1, false: 2 }
  return devices.value.slice().sort((d1, d2) =>
    order[d1.data.online] > order[d2.data.online] ? 1 : -1
  )
})

const loginForm = ref({ username: '', password: '' })

onMounted(async () => {
  loginState.value = false
  devices.value = []
})

const login = async () => {
  try {
    // Login to app
    await authService.login(
      loginForm.value.username,
      loginForm.value.password
    )
    
    loginState.value = true
    loginForm.value = { username: '', password: '' }
    
    // Load devices
    refreshDevices()
  } catch (err) {
    if (err.response && err.response.status === 401) {
      ElMessage.error('ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง')
    } else if (err.response && err.response.status === 429) {
      ElMessage.error('พยายาม login มากเกินไป กรุณารอสักครู่')
    } else {
      ElMessage.error(`Oops, login error. (${err.message})`)
    }
  }
}

const logout = async () => {
  try {
    await authService.logout()
  } catch (err) {
    console.error('Logout error:', err)
  }
  
  loginState.value = false
  loginForm.value = { username: '', password: '' }
  devices.value = []
}

const refreshDevices = async () => {
  if (isRefreshing.value) {
    ElMessage.warning('กำลังโหลดข้อมูลอยู่ กรุณารอสักครู่')
    return
  }

  try {
    isRefreshing.value = true
    const response = await tuyaService.getDevices()
    
    // Show cache info
    if (response.stale) {
      console.log(`[UI] Using stale cache due to rate limit (${response.cache_age}s old)`)
      ElMessage.warning('ใช้ข้อมูลเก่าเนื่องจากเรียก API บ่อยเกินไป')
    } else if (response.cached) {
      console.log(`[UI] Using cached device list (${response.cache_age}s old)`)
    } else {
      console.log('[UI] Fetched fresh device list from server')
    }
    
    const discoveryDevices = response.payload?.devices || []
    devices.value = discoveryDevices
  } catch (err) {
    if (err.response && err.response.status === 401) {
      loginState.value = false
      devices.value = []
      ElMessage.error('กรุณา login อีกครั้ง')
    } else if (err.response && err.response.status === 429) {
      ElMessage.error('เรียก API บ่อยเกินไป กรุณารอสักครู่แล้วลองใหม่')
    } else if (err.response && err.response.status === 503) {
      ElMessage.error('ไม่สามารถเชื่อมต่อกับระบบได้ กรุณาลองใหม่อีกครั้ง')
    } else {
      ElMessage.error(`Oops, device discovery error. (${err.message})`)
    }
  } finally {
    isRefreshing.value = false
  }
}

const toggleDevice = async (device) => {
  try {
    const newState = !device.data.state
    await tuyaService.controlDevice(device.id, 'turnOnOff', newState)
    device.data.state = newState
  } catch (err) {
    if (err.response && err.response.status === 401) {
      loginState.value = false
      devices.value = []
      ElMessage.error('กรุณา login อีกครั้ง')
    } else {
      ElMessage.error(`Oops, device control error. (${err.message})`)
    }
  }
}

const triggerScene = async (device) => {
  try {
    await tuyaService.controlDevice(device.id, 'turnOnOff', true)
  } catch (err) {
    if (err.response && err.response.status === 401) {
      loginState.value = false
      devices.value = []
      ElMessage.error('กรุณา login อีกครั้ง')
    } else {
      ElMessage.error(`Oops, device control error. (${err.message})`)
    }
  }
}
</script>

<style scoped>
#nav {
  margin: 0 auto;
  margin-top: 64px;
  margin-bottom: 64px;
}

.el-card.device {
  max-width: 800px;
  margin: 0 auto;
  margin-bottom: 16px;
}
.el-card.device :deep(.el-card__body) {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.el-card.device :deep(.el-card__body :last-child) {
  margin-left: auto;
}

.el-button.state-on:enabled {
  color: #f9f9f9;
  background-color: #7dd8ba;
}
.el-button.state-off:enabled {
  color: #a3a4a7;
  background-color: #f9f9f9;
}
.el-button.trigger:enabled {
  color: #f9f9f9;
  background-color: #9eabce;
}
.el-button.el-button--large {
  padding: 9px;
  font-size: 20px;
  line-height: 0px;
}


.el-avatar {
  background: transparent;
  margin-right: 16px;
}
</style>
